<template>
  <div class="ds-sidebar-form">
    <!-- Workspace -->
    <div v-if="showWorkspace" class="ds-field">
      <label class="ds-label">Project</label>
      <div class="ds-input">
        <QuickAddWorkspace
          :value="workspace"
          @input="$emit('update:workspace', $event)"
          :profile="profile"
          size="sm"
        />
      </div>
    </div>

    <div class="ds-field">
      <label class="ds-label">Quantity</label>
      <div class="ds-input">
        <b-form-spinbutton
          :value="quantity"
          @input="updateQuantity"
          min="1"
          max="999"
          size="sm"
        />
      </div>
    </div>

    <div class="ds-field">
      <label class="ds-label">Location</label>
      <div class="ds-input">
        <b-form-input
          :value="location"
          @input="updateLocation"
          placeholder="Enter location"
          trim
          size="sm"
        />
      </div>
    </div>

    <!-- Dimensions -->
    <div class="ds-field">
      <label class="ds-label">Size</label>
      <div class="ds-input">
        <div class="d-flex align-items-center">
          <b-form-input
            :value="dimensions.width"
            @input="updateDimension('width', $event)"
            type="number"
            placeholder="W"
            size="sm"
            :state="getFieldError('config_data.width', 'width') ? false : null"
          ></b-form-input>
          <span class="mx-1 small text-muted">&times;</span>
          <b-form-input
            :value="dimensions.height"
            @input="updateDimension('height', $event)"
            type="number"
            placeholder="H"
            size="sm"
            :state="getFieldError('config_data.height', 'height') ? false : null"
          ></b-form-input>
        </div>
        <div v-if="getFieldError('config_data.width', 'width')" class="ds-error">{{ getFieldError('config_data.width', 'width') }}</div>
        <div v-if="getFieldError('config_data.height', 'height')" class="ds-error">{{ getFieldError('config_data.height', 'height') }}</div>
      </div>
    </div>

    <!-- Substrate -->
    <div class="ds-field">
      <label class="ds-label">Substrate</label>
      <div class="ds-input">
        <b-form-select :value="selectedSubstrate" @input="onSubstrateChange($event)" :options="substrateOptions" size="sm">
          <template #first>
            <b-form-select-option :value="null" disabled>-- Select --</b-form-select-option>
          </template>
        </b-form-select>
      </div>
    </div>

    <!-- Options -->
    <template v-if="selectedSubstrate">
      <div v-if="loadingOptions" class="text-center py-2">
        <b-spinner small label="Loading..."></b-spinner>
      </div>
      <div v-else-if="availableOptions.length === 0" class="small text-muted py-1">
        No options available.
      </div>
      <template v-else>
        <hr class="my-2">

        <div class="ds-field">
          <label class="ds-label">Image ID</label>
          <div class="ds-input">
            <div class="d-flex align-items-center gap-2">
              <router-link
                v-if="selectedImage && selectedImage.thumb"
                :to="imageSearchRoute"
                :title="selectedImage.title"
                class="ds-image-meta"
              >
                <b-img :src="selectedImage.thumb" rounded class="ds-image-thumb mr-1" />
              </router-link>
              <b-form-input
                :value="imageIdInput"
                @input="imageIdInput = $event"
                @change="onImageIdCommit"
                trim
                size="sm"
                :state="imageIdError ? false : null"
              />
            </div>
            <div v-if="imageIdError" class="ds-error">{{ imageIdError }}</div>
          </div>
          <div v-if="pricingEnabled" class="ds-price text-right text-nowrap">
            <span v-if="getItemizedPrice('image') !== null">${{ formatPrice(getItemizedPrice('image')) }}</span>
          </div>
        </div>

        <div v-for="(options, category) in groupedOptions" :key="category">
          <!-- Subcategory filter -->
          <div v-if="getSubcategories(options).length > 0" :key="category + '-sub'" class="ds-field">
            <label class="ds-label">{{ category }} Family</label>
            <div class="ds-input">
              <b-form-select
                :value="selectedSubcategories[category]"
                @input="updateSubcategory(category, $event)"
                :options="getSubcategories(options)"
                :disabled="isCategoryDisabled(category)"
                size="sm"
              >
                <template #first>
                  <b-form-select-option :value="null">All</b-form-select-option>
                </template>
              </b-form-select>
            </div>
            <div v-if="pricingEnabled" class="ds-price"></div>
          </div>

          <!-- Option select -->
          <div class="ds-field">
            <label class="ds-label" :title="category">{{ category }}</label>
            <div class="ds-input">
              <b-form-select
                :value="selectedOptions[category]"
                @input="updateOption(category, $event)"
                :options="getCategoryOptionsFiltered(options, category)"
                :disabled="isCategoryDisabled(category)"
                :state="getOptionError(category) ? false : null"
                size="sm"
              >
                <template #first>
                  <b-form-select-option :value="null">-- Select --</b-form-select-option>
                </template>
              </b-form-select>
              <div v-if="getOptionError(category)" class="ds-error">{{ getOptionError(category) }}</div>
            </div>
            <div v-if="pricingEnabled" class="ds-price text-right text-nowrap">
              <span v-if="getItemizedPrice(category) !== null">${{ formatPrice(getItemizedPrice(category)) }}</span>
            </div>
          </div>

          <!-- Frame images (below Frame select) -->
          <div
            v-if="isFrameCategory(category) && computedFrameData"
            :key="category + '-images'"
            class="ds-frame-images"
          >
            <label class="ds-label small text-muted">Frame images</label>
            <div class="d-flex flex-wrap gap-2 mt-1">
              <div
                v-for="key in frameImageKeys"
                :key="key"
                v-show="computedFrameData[key]"
                class="ds-frame-img"
              >
                <img :src="computedFrameData[key]" :alt="key" class="rounded" />
                <small class="d-block text-muted mt-1">{{ key }}</small>
              </div>
            </div>
          </div>
        </div>

        <details v-if="pricingEnabled && miscOptions.length" class="mb-2 ds-misc-details">
          <summary class="ds-misc-summary">
            <span>Misc ({{ miscOptions.length }} items)</span>
            <span v-if="getItemizedPrice('misc') !== null" class="text-dark ds-misc-total">
              ${{ formatPrice(getItemizedPrice('misc')) }}
            </span>
          </summary>
          <div class="mt-1">
            <div v-for="opt in miscOptions" :key="`misc-${opt.id}`" class="ds-field mb-1">
              <label class="ds-label"></label>
              <div class="ds-input ds-small text-muted" :title="getOptionLabel(opt)">{{ getOptionLabel(opt) }}</div>
              <div class="ds-price text-right text-nowrap">
                <span v-if="getMiscOptionPrice(opt) !== null">${{ formatPrice(getMiscOptionPrice(opt)) }}</span>
              </div>
            </div>
          </div>
        </details>

        <div v-if="pricingEnabled && getItemizedPrice('Source adjustment') !== null" class="ds-field mb-2">
          <label class="ds-label" style="width: auto;">Source adjustment</label>
          <div class="ds-input text-muted"></div>
          <div class="ds-price text-right text-nowrap">
            ${{ formatPrice(getItemizedPrice('Source adjustment')) }}
          </div>
        </div>
      </template>
    </template>

    <template v-if="pricingEnabled && priceData">
      <hr class="my-2">
      <div v-if="priceData.unit_sell_price" class="ds-field">
        <label class="ds-label">Unit Sell</label>
        <div class="ds-input"></div>
        <div class="ds-price text-right text-nowrap">${{ formatPrice(priceData.unit_sell_price) }}</div>
      </div>
      <hr class="my-2">
      <div v-if="priceData.unit_sell_price || priceData.total_sell_price" class="ds-field">
        <label class="ds-label">Total Sell</label>
        <div class="ds-input ds-small text-right">{{ priceData.unit_sell_price ? `$${formatPrice(priceData.unit_sell_price)}` : '—' }} &times; {{ priceData.quantity }} = </div>
        <div class="ds-price text-right font-weight-bold">{{ priceData.total_sell_price ? `$${formatPrice(priceData.total_sell_price)}` : '—' }}</div>
      </div>
      <div v-if="priceData.unit_net_price || priceData.total_net_price" class="ds-field">
        <label class="ds-label">Total Net</label>
        <div class="ds-input ds-small text-right">{{ priceData.unit_net_price ? `$${formatPrice(priceData.unit_net_price)}` : '—' }} &times; {{ priceData.quantity }} = </div>
        <div class="ds-price text-right">{{ priceData.total_net_price ? `$${formatPrice(priceData.total_net_price)}` : '—' }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import { groupBy } from 'lodash'
import QuickAddWorkspace from '@/components/QuickAddWorkspace.vue'

export default {
  name: 'DesignStudioItemForm',
  components: {
    QuickAddWorkspace,
  },
  props: {
    substrates: { type: Array, default: () => [] },
    selectedSubstrate: { default: null },
    availableOptions: { type: Array, default: () => [] },
    selectedOptions: { type: Object, default: () => ({}) },
    selectedSubcategories: { type: Object, default: () => ({}) },
    dimensions: { type: Object, default: () => ({ width: '', height: '' }) },
    workspace: { type: Object, default: null },
    quantity: { type: [Number, String], default: 1 },
    location: { type: String, default: '' },
    profile: { type: Object, default: null },
    loadingSubstrates: { type: Boolean, default: false },
    loadingOptions: { type: Boolean, default: false },
    validationErrors: { type: Object, default: () => ({}) },
    showWorkspace: { type: Boolean, default: true },
    priceData: { type: Object, default: null },
    pricingEnabled: { type: Boolean, default: false },
    selectedImage: { type: Object, default: null },
    imageIdError: { type: String, default: '' },
  },
  data() {
    return {
      frameImageKeys: ['front_image', 'side_image', 'top_image'],
      imageIdInput: '',
    }
  },
  computed: {
    substrateOptions() {
      return this.substrates.map(s => ({
        value: s.id,
        text: s.name,
      }))
    },
    groupedOptions() {
      return groupBy(
        this.availableOptions.filter(opt => !this.isMiscCategory(opt?.name)),
        'name'
      )
    },
    miscOptions() {
      return this.availableOptions.filter(opt => this.isMiscCategory(opt?.name))
    },
    itemizedEntries() {
      if (!this.pricingEnabled) return []
      const itemized = this.priceData?.itemized
      if (!itemized || typeof itemized !== 'object') return []
      return Object.entries(itemized)
    },
    itemizedEntryMap() {
      const out = {}
      this.itemizedEntries.forEach(([key, val]) => {
        out[this.normalizeItemizedKey(key)] = val
      })
      return out
    },
    currentSubstrateName() {
      const sub = this.substrates.find(s => s.id === this.selectedSubstrate)
      return sub ? sub.name : ''
    },
    /**
     * Frame extra_data from the selected Frame option.
     */
    computedFrameData() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/frame/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt && opt.extra_data && opt.extra_data.top_image) {
          return opt.extra_data
        }
      }
      return null
    },
    /** Number of mats (0, 1, 2, or 3) from "Number of mats" option */
    computedNumberOfMats() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/number\s*of\s*mats/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (!opt) continue
        const v = parseInt(opt.value ?? opt.num_value ?? opt.char_value, 10)
        return isNaN(v) ? undefined : Math.max(0, Math.min(3, v))
      }
      return undefined
    },
    /** Single-mat backwards compatibility */
    computedMatData() {
      const arr = this.computedMatDataArray
      return arr && arr.length > 0 ? arr[0] : null
    },
    /** Array of mat data for Mat 1, Mat 2, Mat 3 */
    computedMatDataArray() {
      const result = []
      for (const n of [1, 2, 3]) {
        const re = new RegExp(`^Mat\\s*${n}$`, 'i')
        for (const [category, optionId] of Object.entries(this.selectedOptions)) {
          if (!optionId || !re.test(category)) continue
          const opt = this.availableOptions.find(o => o.id === optionId)
          if (opt?.extra_data?.color) {
            result.push({ color: opt.extra_data.color })
          }
          break
        }
      }
      return result
    },
    /** Per-side mat widths from options */
    computedMatWidth() {
      const arr = this.computedMatDataArray
      if (!arr?.length) return { top: 0, bottom: 0, left: 0, right: 0 }
      const perMat = this.computedMatWidthsArray
      if (perMat?.length === 1) return perMat[0]
      if (perMat?.length) {
        const sum = perMat.reduce((a, w) => ({
          top: a.top + w.top, bottom: a.bottom + w.bottom,
          left: a.left + w.left, right: a.right + w.right,
        }), { top: 0, bottom: 0, left: 0, right: 0 })
        const n = perMat.length
        const revealPerSide = (n - 1) * 0.25
        return {
          top: sum.top + revealPerSide,
          bottom: sum.bottom + revealPerSide,
          left: sum.left + revealPerSide,
          right: sum.right + revealPerSide,
        }
      }
      return { top: 0, bottom: 0, left: 0, right: 0 }
    },
    /** Per-mat widths array */
    computedMatWidthsArray() {
      const matDataArr = this.computedMatDataArray
      if (!matDataArr?.length) return []
      const total = { top: 2, bottom: 2, left: 2, right: 2 }
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/mat\s*width/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (!opt) continue
        const val = parseFloat(opt.value)
        if (isNaN(val)) continue
        const lower = category.toLowerCase()
        if (lower.includes('top')) total.top = val
        else if (lower.includes('bottom')) total.bottom = val
        else if (lower.includes('left')) total.left = val
        else if (lower.includes('right')) total.right = val
        else {
          total.top = total.bottom = total.left = total.right = val
        }
      }
      return matDataArr.map((_, i) =>
        i === 0
          ? { top: total.top, bottom: total.bottom, left: total.left, right: total.right }
          : { top: 0.25, bottom: 0.25, left: 0.25, right: 0.25 }
      )
    },
    /** Canvas mount style */
    computedCanvasMount() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/canvas\s*mount/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt) {
          const val = opt.value != null ? String(opt.value) : opt.name || ''
          return val || null
        }
      }
      return null
    },
    /** Float frame extra_data */
    computedFloatFrameData() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/float\s*frame/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt && opt.extra_data) return opt.extra_data
      }
      return null
    },
    /** Stretcher depth in inches (Canvas) */
    computedStretcherDepth() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/stretcher/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (!opt) continue
        const val = opt.value != null ? String(opt.value) : opt.name || ''
        const parsed = parseFloat(val.replace(/["']/g, ''))
        return isNaN(parsed) ? null : parsed
      }
      return null
    },
    /** Substrate back data */
    computedSubstrateBackData() {
      const sub = String(this.currentSubstrateName || '').toLowerCase()
      if (!sub) return null

      const getOptionValue = (pattern) => {
        for (const [category, optionId] of Object.entries(this.selectedOptions)) {
          if (!optionId) continue
          if (!pattern.test(category)) continue
          const opt = this.availableOptions.find(o => o.id === optionId)
          return opt ? (opt.value != null ? String(opt.value) : opt.name || '') : null
        }
        return null
      }
      const parseInches = (val) => {
        if (val == null) return null
        const s = String(val).replace(/["']/g, '').trim()
        const frac = s.match(/^(\d+)\s*\/\s*(\d+)/)
        if (frac) return Number(frac[1]) / Number(frac[2])
        const parsed = parseFloat(s)
        return isNaN(parsed) ? null : parsed
      }

      if (/aluminum/i.test(sub)) {
        const material = getOptionValue(/aluminum\s*material/i) || ''
        const isWhite = /white/i.test(material)
        const border = parseInches(getOptionValue(/border/i)) ?? 0
        return { depth: 0.25, color: isWhite ? 0xffffff : 0xc0c0c0, roughness: 0.4, metalness: 0.6, hasSides: true, border }
      }
      if (/acrylic/i.test(sub)) {
        const acrylicVal = getOptionValue(/acrylic\s*substrate/i)
        const depth = parseInches(acrylicVal) ?? 0.25
        const border = parseInches(getOptionValue(/border/i)) ?? 0
        const colorFromName = (val) => {
          if (!val) return null
          const s = String(val).toLowerCase()
          if (/black/i.test(s)) return 0x2a2a2a
          if (/white/i.test(s)) return 0xffffff
          if (/clear|transparent/i.test(s)) return 0xb8d4e3
          if (/silver|grey|gray/i.test(s)) return 0xc0c0c0
          if (/bronze/i.test(s)) return 0xcd7f32
          if (/gold/i.test(s)) return 0xd4af37
          return null
        }
        return { depth, color: colorFromName(acrylicVal) ?? 0xb8d4e3, roughness: 0.1, metalness: 0.3, hasSides: true, border }
      }
      if (/acoustic/i.test(sub)) return { depth: 0.25, color: 0xf5f5f5, roughness: 0.7, metalness: 0 }
      if (/gator/i.test(sub)) return { depth: 0.125, color: 0x5a5a5a, roughness: 0.6, metalness: 0, hasSides: true }
      if (/mural/i.test(sub)) return { depth: 0.1, color: 0xffffff, roughness: 0.9, metalness: 0 }
      if (/window\s*film/i.test(sub)) {
        const material = (getOptionValue(/material/i) || getOptionValue(/window\s*film/i) || '').toLowerCase()
        const isOpaque = /opaque/i.test(material)
        const isTransparent = /transparent/i.test(material)
        const isTranslucent = /translucent/i.test(material)
        const isPerforated = /perforat/i.test(material)
        if (isOpaque) return { depth: 0.1, color: 0xffffff, roughness: 0.9, metalness: 0 }
        return {
          depth: 0,
          useBackface: isTransparent || isTranslucent || isPerforated,
          opacity: isTransparent ? 0.65 : isTranslucent ? 0.9 : 1,
          perforated: isPerforated,
          holesPerInch: 4,
          holeSizeInches: 0.0625,
        }
      }
      return null
    },
    /** Hang hardware data */
    computedHangData() {
      for (const [category, optionId] of Object.entries(this.selectedOptions)) {
        if (!optionId) continue
        if (!/hang/i.test(category)) continue
        const opt = this.availableOptions.find(o => o.id === optionId)
        if (opt) {
          const name = this.getOptionLabel(opt)
          return { name }
        }
      }
      return null
    },
    /** Full frame configuration object emitted via frame-config-changed */
    frameConfig() {
      return {
        substrate: this.currentSubstrateName,
        frameData: this.computedFrameData,
        matData: this.computedMatData,
        matDataArray: this.computedMatDataArray,
        matWidth: this.computedMatWidth,
        matWidthsArray: this.computedMatWidthsArray,
        hangData: this.computedHangData,
        stretcherDepth: this.computedStretcherDepth,
        canvasMount: this.computedCanvasMount,
        floatFrameData: this.computedFloatFrameData,
        substrateBackData: this.computedSubstrateBackData,
      }
    },
    imageSearchRoute() {
      return {
        name: 'search',
        query: {
          q: this.selectedImage?.local_id,
          debug: (this.selectedImage?.published ? undefined : 1),
        },
      }
    },
  },
  watch: {
    frameConfig: {
      handler(val) {
        this.$emit('frame-config-changed', val)
      },
      deep: true,
      immediate: true,
    },
    computedNumberOfMats: {
      handler() {
        this.pruneInactiveMatSelections()
      },
      immediate: true,
    },
    availableOptions: {
      handler() {
        this.pruneInactiveMatSelections()
      },
      immediate: true,
    },
    selectedImage: {
      handler(image) {
        this.imageIdInput = this.getImageIdentifier(image)
      },
      immediate: true,
    },
  },
  methods: {
    formatPrice(val) {
      const n = parseFloat(val)
      return isNaN(n) ? val : n.toFixed(2)
    },
    normalizeItemizedKey(value) {
      return String(value || '').trim().toLowerCase()
    },
    getItemizedPrice(category) {
      const normalizedCategory = this.normalizeItemizedKey(category)
      return Object.prototype.hasOwnProperty.call(this.itemizedEntryMap, normalizedCategory)
        ? this.itemizedEntryMap[normalizedCategory]
        : null
    },
    getMiscOptionPrice(opt) {
      if (!opt) return null
      const optionLabel = this.getOptionLabel(opt)
      const normalizedOptionLabel = this.normalizeItemizedKey(optionLabel)

      const debugOptionsPricing = this.priceData?.debug?.options_pricing
      if (Array.isArray(debugOptionsPricing)) {
        const matchingDebugRows = debugOptionsPricing.filter(row => {
          const nameMatches = this.normalizeItemizedKey(row?.name) === 'misc'
          const valueMatches = this.normalizeItemizedKey(row?.value) === normalizedOptionLabel
          return nameMatches && valueMatches
        })

        if (matchingDebugRows.length) {
          const total = matchingDebugRows.reduce((sum, row) => {
            const raw = row?.computed_price != null ? row.computed_price : row?.base_price
            const n = parseFloat(raw)
            return isNaN(n) ? sum : sum + n
          }, 0)
          if (!isNaN(total) && total > 0) {
            return total
          }
          const firstRaw = matchingDebugRows[0]?.computed_price != null
            ? matchingDebugRows[0].computed_price
            : matchingDebugRows[0]?.base_price
          if (firstRaw != null) {
            return firstRaw
          }
        }
      }

      const categoryName = String(opt.name || '').trim()
      const candidates = [
        optionLabel,
        `${categoryName} ${optionLabel}`,
        `${categoryName}: ${optionLabel}`,
      ]
      for (const candidate of candidates) {
        const key = this.normalizeItemizedKey(candidate)
        if (Object.prototype.hasOwnProperty.call(this.itemizedEntryMap, key)) {
          return this.itemizedEntryMap[key]
        }
      }
      return null
    },
    getImageIdentifier(image = this.selectedImage) {
      if (!image || typeof image !== 'object') return ''
      return String(image.id || image._identifier || image.identifier || '').trim()
    },
    onImageIdCommit() {
      this.$emit('image-id-change', String(this.imageIdInput || '').trim())
    },
    normalizeOptionValue(value) {
      return String(value ?? '').trim().toLowerCase()
    },
    getTopsailWhiteOptionId(options) {
      const target = 'topsail white'
      const match = (options || []).find((option) => {
        const label = this.normalizeOptionValue(option?.value ?? option?.char_value ?? option?.num_value ?? option?.name)
        return label === target
      })
      return match?.id ?? null
    },
    getCategoryMatNumber(category) {
      const label = String(category || '')
      const match = label.match(/\bmat\s*(\d+)\b/i)
      if (!match) return null
      const value = parseInt(match[1], 10)
      return Number.isFinite(value) ? value : null
    },
    isCategoryDisabled(category, matCount = this.computedNumberOfMats) {
      if (matCount == null || !Number.isFinite(matCount)) return false
      if (matCount === 0 && /mat\s*width/i.test(String(category || ''))) return true
      const matNum = this.getCategoryMatNumber(category)
      return matNum != null && matNum > matCount
    },
    pruneInactiveMatSelections() {
      const matCount = this.computedNumberOfMats
      if (matCount == null || !Number.isFinite(matCount)) return

      const nextOptions = {}
      let optionsChanged = false
      for (const [category, optionId] of Object.entries(this.selectedOptions || {})) {
        if (this.isCategoryDisabled(category, matCount)) {
          optionsChanged = true
          continue
        }
        nextOptions[category] = optionId
      }

      const orderedCategories = Object.entries(this.groupedOptions || {}).sort(([a], [b]) => {
        const aMat = this.getCategoryMatNumber(a)
        const bMat = this.getCategoryMatNumber(b)
        if (aMat != null && bMat != null) return aMat - bMat
        if (aMat != null) return -1
        if (bMat != null) return 1
        return String(a).localeCompare(String(b))
      })

      for (const [category, options] of orderedCategories) {
        if (this.isCategoryDisabled(category, matCount)) continue
        const isMatWidthCategory = /mat\s*width/i.test(String(category || ''))
        const matNum = this.getCategoryMatNumber(category)
        if (matNum == null && !isMatWidthCategory) continue
        const hasSelection = nextOptions[category] != null
        if (hasSelection) continue

        const defaultOpt = (options || []).find(o => o && o.is_default)
        if (defaultOpt) {
          nextOptions[category] = defaultOpt.id
          optionsChanged = true
          continue
        }

        const preferredId = matNum === 3 ? this.getTopsailWhiteOptionId(options) : null
        if (preferredId != null) {
          nextOptions[category] = preferredId
          optionsChanged = true
        }
      }

      if (optionsChanged) {
        this.$emit('update:selectedOptions', nextOptions)
      }

      const nextSubcategories = {}
      let subcategoriesChanged = false
      for (const [category, subcategory] of Object.entries(this.selectedSubcategories || {})) {
        if (this.isCategoryDisabled(category, matCount)) {
          subcategoriesChanged = true
          continue
        }
        nextSubcategories[category] = subcategory
      }

      if (subcategoriesChanged) {
        this.$emit('update:selectedSubcategories', nextSubcategories)
      }
    },
    isMiscCategory(category) {
      return String(category || '').trim().toLowerCase() === 'misc'
    },
    isFrameCategory(category) {
      return category && /frame/i.test(category)
    },
    getSubcategories(options) {
      const groups = new Set()
      options.forEach(opt => {
        const cGroups = opt.extra_data && opt.extra_data.color_groups
        if (Array.isArray(cGroups)) {
          cGroups.forEach(g => groups.add(g))
        }
      })
      return Array.from(groups).sort().map(g => ({ value: g, text: g }))
    },
    getCategoryOptionsFiltered(options, category) {
      const selectedSub = this.selectedSubcategories[category]
      let filteredOptions = options
      if (selectedSub) {
        filteredOptions = options.filter(opt => {
          const cGroups = opt.extra_data && opt.extra_data.color_groups
          return Array.isArray(cGroups) && cGroups.includes(selectedSub)
        })
      }
      return filteredOptions.map(opt => ({
        value: opt.id,
        text: this.getOptionLabel(opt),
      }))
    },
    getOptionLabel(opt) {
      const val = opt.value
      if (val === null || val === undefined || val === '') return opt.name
      if (typeof val === 'number') return String(val)
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      return String(val)
    },
    getFieldError(...keys) {
      for (const key of keys) {
        if (this.validationErrors[key]) return this.validationErrors[key]
      }
      return null
    },
    getOptionError(category) {
      const prefix = `config_data.${category}.`
      const msgs = []
      Object.entries(this.validationErrors).forEach(([key, msg]) => {
        if (key.startsWith(prefix) || key === `config_data.${category}`) {
          msgs.push(msg)
        }
      })
      return msgs.length ? msgs.join(', ') : null
    },
    updateDimension(key, value) {
      this.$emit('update:dimensions', { ...this.dimensions, [key]: value })
    },
    updateQuantity(value) {
      this.$emit('update:quantity', value)
    },
    updateLocation(value) {
      this.$emit('update:location', value)
    },
    updateOption(category, value) {
      if (this.isCategoryDisabled(category)) return
      this.$emit('update:selectedOptions', { ...this.selectedOptions, [category]: value })
    },
    updateSubcategory(category, value) {
      if (this.isCategoryDisabled(category)) return
      this.$emit('update:selectedSubcategories', { ...this.selectedSubcategories, [category]: value })
    },
    onSubstrateChange(value) {
      this.$emit('update:selectedSubstrate', value)
      this.$emit('substrate-changed', value)
    },
  },
}
</script>

<style scoped>
.ds-field {
  display: flex;
  align-items: center;
  margin-bottom: 0.35rem;
}

.ds-label {
  flex: 0 0 auto;
  width: 68px;
  min-width: 68px;
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 0.35rem;
  line-height: 1.8;
  text-transform: capitalize;
}

.ds-input {
  flex: 1 1 auto;
  min-width: 0;
}

.ds-price {
  flex: 0 0 auto;
  width: 90px;
  min-width: 90px;
  font-size: 0.75rem;
  padding-left: 0.35rem;
  line-height: 1.8;
}

.ds-small {
  font-size: 0.75rem;
}

.ds-sidebar-form hr {
  margin: 0.4rem 0;
}

.ds-sidebar-form .btn-sm {
  font-size: 0.72rem;
  padding: 0.2rem 0.5rem;
}

.ds-sidebar-form >>> .custom-select-sm,
.ds-sidebar-form >>> .form-control-sm {
  font-size: 0.75rem;
  padding: 0.15rem 0.4rem;
  height: calc(1.5em + 0.3rem + 2px);
}

.ds-error {
  font-size: 0.68rem;
  color: #dc3545;
  margin-top: 0.1rem;
  line-height: 1.3;
}

.ds-frame-images {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.35rem;
}

.ds-frame-images .ds-label {
  flex: 0 0 auto;
}

.ds-frame-img {
  text-align: center;
}

.ds-frame-img img {
  max-width: 80px;
  max-height: 48px;
  object-fit: contain;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.ds-frame-img small {
  font-size: 0.6rem;
}

.ds-misc-summary {
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.8;
  cursor: pointer;
}

.ds-misc-total {
  float: right;
}

.ds-misc-summary:focus {
  outline: none;
}

.ds-image-meta {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 170px;
  color: #6c757d;
  font-size: 0.72rem;
  text-decoration: none;
}

.ds-image-thumb {
  height: 24px;
  width: auto;
}
</style>
